import { FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CHART_DESCRIPTION_MAX_LENGTH,
  CHART_DESCRIPTION_MIN_LENGTH,
  type ChartRuleResolveError,
} from '@/entities/dashboards';
import { AiTextarea } from '@/shared/ui';
import type { PresetChartFormValues } from '../../model/dashboardPresetForm';
import { useChartRuleResolution } from '../../model/useChartRuleResolution';
import { CHART_DESCRIPTION_FIELD_CLASS } from './chartDescriptionField';

type Props = {
  /** Заводимая диаграмма: правила за ней ещё нет — есть только место на сетке */
  chart: PresetChartFormValues;
  /** Описание истолковано — диаграмма с правилом и оформлением встаёт на своё место */
  onResolve: (chart: PresetChartFormValues) => void;
};

/**
 * Единственное поле блока свойств заводимой диаграммы — описание нужных данных. Прочих полей до
 * истолкования нет: название, тип и размер даёт система, и заполнять их значило бы просить
 * пользователя угадать, что получится.
 *
 * Описание истолковывается по потере фокуса, и вместе с правилом приходят название, тип и размер
 * диаграммы. Отказ показан здесь же, а введённое описание остаётся — его есть чем уточнить.
 */
export const NewChartDescriptionField: FC<Props> = ({ chart, onResolve }) => {
  const { t } = useTranslation('qualityDashboard');
  const { resolveNewChart, isResolving } = useChartRuleResolution();

  const [description, setDescription] = useState(chart.description);
  const [error, setError] = useState<ChartRuleResolveError | null>(null);
  // Описание, с которым уже ходили за истолкованием: клик мимо поля повторного обращения к
  // модели не стоит. После отказа повтор разрешён — причиной мог быть и сбой связи.
  const resolvedDescriptionRef = useRef<string | null>(null);

  const handleBlur = async () => {
    const text = description.trim();
    if (text.length < CHART_DESCRIPTION_MIN_LENGTH) return;
    if (text === resolvedDescriptionRef.current && !error) return;
    resolvedDescriptionRef.current = text;
    const outcome = await resolveNewChart(chart, text);
    setError(outcome.error);
    if (outcome.error) return;
    onResolve(outcome.chart);
  };

  return (
    <div className="flex flex-col gap-2">
      <AiTextarea
        autoFocus
        isProcessing={isResolving}
        value={description}
        onChange={(event) => setDescription(event.target.value)}
        onBlur={handleBlur}
        label={t('presets.editor.chartDescription')}
        placeholder={t('presets.editor.chartDescriptionPlaceholder')}
        maxLength={CHART_DESCRIPTION_MAX_LENGTH}
        disabled={isResolving}
        classNames={{ input: CHART_DESCRIPTION_FIELD_CLASS }}
      />
      {error && <p className="text-sm text-destructive">{error.message || t('presets.editor.resolveFailed')}</p>}
    </div>
  );
};
